import { Logger } from "./Logger";
import {
  loadPersistedNamespaceFilters,
  setNamespaceFilters,
} from "./namespaceFilter";
import { ConsoleTransport } from "./transports/ConsoleTransport";
import { LogLevel, type LoggerOptions, type Transport } from "./types";

const LEVEL_STORAGE_KEY = "__risa_logger_level";

interface InternalConfig {
  level: LogLevel;
  transports: Transport[];
  enabled: boolean;
  historySize: number;
  breadcrumbSize: number;
  persistConfig: boolean;
}

function isDevelopment(): boolean {
  try {
    // Check NODE_ENV (set by webpack/CRA DefinePlugin)
    if (process.env.NODE_ENV === "development") return true;
  } catch {
    // process.env may not exist
  }
  try {
    // Check REACT_APP_ENV (CRA custom env var)
    if (process.env.REACT_APP_ENV === "development") return true;
  } catch {
    // may not exist
  }
  return false;
}

function detectDefaultLevel(): LogLevel {
  return isDevelopment() ? LogLevel.DEBUG : LogLevel.SILENT;
}

function loadPersistedLevel(): LogLevel | null {
  try {
    const stored = localStorage.getItem(LEVEL_STORAGE_KEY);
    if (stored !== null) {
      const num = Number(stored);
      if (num >= LogLevel.DEBUG && num <= LogLevel.SILENT) {
        return num as LogLevel;
      }
    }
  } catch {
    // localStorage unavailable
  }
  return null;
}

function persistLevel(level: LogLevel): void {
  try {
    localStorage.setItem(LEVEL_STORAGE_KEY, String(level));
  } catch {
    // localStorage unavailable
  }
}

const defaultLevel = detectDefaultLevel();
const persistedLevel = loadPersistedLevel();

const config: InternalConfig = {
  level: persistedLevel ?? defaultLevel,
  transports: [new ConsoleTransport()],
  enabled: true,
  historySize: 100,
  breadcrumbSize: 20,
  persistConfig: defaultLevel === LogLevel.DEBUG,
};

if (config.persistConfig) {
  loadPersistedNamespaceFilters();
}

export function getConfig(): Readonly<InternalConfig> {
  return config;
}

export function configureLogger(options: LoggerOptions): void {
  if (options.level !== undefined) {
    config.level = options.level;
    if (config.persistConfig) persistLevel(config.level);
  }
  if (options.transports !== undefined) {
    config.transports = options.transports;
  }
  if (options.enabled !== undefined) {
    config.enabled = options.enabled;
  }
  if (options.historySize !== undefined) {
    config.historySize = options.historySize;
  }
  if (options.breadcrumbSize !== undefined) {
    config.breadcrumbSize = options.breadcrumbSize;
  }
  if (options.persistConfig !== undefined) {
    config.persistConfig = options.persistConfig;
  }
  if (options.enabledNamespaces || options.disabledNamespaces) {
    setNamespaceFilters(
      options.enabledNamespaces ?? ["*"],
      options.disabledNamespaces ?? [],
    );
  }
}

export function setLevel(level: LogLevel): void {
  config.level = level;
  if (config.persistConfig) persistLevel(level);
}

export function createLogger(namespace: string, fileName?: string): Logger {
  return new Logger(namespace, fileName);
}

export { LogLevel };
